import { action, computed, makeObservable, observable } from 'mobx';
import { ApiCreateBonusCreationResponseType } from 'src/api_openapi/v2/bonus-engine/apiCreateBonusCreation';
import { ApiGetBonusCreationsResponse200Type } from 'src/api_openapi/v2/bonus-engine/apiGetBonusCreations';
import { ApiUpdateBonusCreationResponseType } from 'src/api_openapi/v2/bonus-engine/apiUpdateBonusCreation';
import { BonusCreationPatchType } from 'src/api_openapi/v2/bonus-engine/schemas';
import { Common } from 'src/AppState/Common';
import { AutoWeakMap } from 'src_common/common/mobx-utils/AutoWeakMap';
import { Resource } from 'src_common/common/mobx-utils/Resource';
import { BonusCreationModel } from './BonusCreationModel';
import { BonusCreationFormType } from 'src/domains/casino/webview/bonus-engine/common/helpers';

export class BonusCreationStore {
    @observable public bonusCreationsResource: Resource<ApiGetBonusCreationsResponse200Type>;
    @observable public isPromoCodeUpdating: boolean = false;

    public static get = AutoWeakMap.create((common: Common) => new BonusCreationStore(common));

    private constructor(private common: Common) {
        this.bonusCreationsResource = new Resource(async (): Promise<ApiGetBonusCreationsResponse200Type> => {
            const response = await this.common.trpcClient.client.bonusCreatorRouter.getBonusCreations.mutate();

            if (response.status === 200) {
                return response.body;
            }
            return [];
        });
        makeObservable(this);
    }

    @computed public get allBonusCreations(): Array<BonusCreationModel> {
        const resource = this.bonusCreationsResource;
        const result = resource.get();
        if (result.type === 'ready') {
            return result.value.map((data) => new BonusCreationModel(data));
        } else {
            return [];
        }
    }

    @action public getBonusCreation = (bonusCreationId: string): BonusCreationModel | null => {
        return this.allBonusCreations.find((bonusReward) => bonusReward.id === bonusCreationId) ?? null;
    };

    @action public createBonusCreation = async (
        bonusReward: BonusCreationFormType
    ): Promise<ApiCreateBonusCreationResponseType> => {
        const response = await this.common.trpcClient.client.bonusCreatorRouter.createBonusCreation.mutate(bonusReward);
        await this.refreshBonusCreations();
        return response;
    };

    @action public updateBonusCreation = async (bonusReward: {
        id: string;
        requestBody: BonusCreationPatchType;
    }): Promise<ApiUpdateBonusCreationResponseType> => {
        const response = await this.common.trpcClient.client.bonusCreatorRouter.updateBonusCreation.mutate({
            id: bonusReward.id,
            requestBody: bonusReward.requestBody,
        });
        await this.refreshBonusCreations();
        return response;
    };

    @action public unpublishBonusCreation = async (id: string): Promise<number> => {
        const response = await this.common.trpcClient.client.bonusCreatorRouter.unpublishBonusCreation.mutate({
            id,
        });
        await this.refreshBonusCreations();
        return response.status;
    };

    @action public deleteBonusCreation = async (id: string): Promise<void> => {
        await this.common.trpcClient.client.bonusCreatorRouter.deleteBonusCreation.mutate({
            id,
        });
        await this.refreshBonusCreations();
    };

    @action public refreshBonusCreations = async (): Promise<void> => {
        await this.bonusCreationsResource.refresh();
    };

    @computed public get isLoading(): boolean {
        const resource = this.bonusCreationsResource;
        const result = resource.get();
        return result.type === 'loading';
    }

    @computed public get hasZeroBonusCreations(): boolean {
        const resource = this.bonusCreationsResource;
        const result = resource.get();
        return result.type === 'ready' && result.value.length === 0;
    }
}
